/**
 * Referência de classes reutilizáveis — agora usando tokens dark-aware.
 * Prefira usar os componentes em `src/components/ui/` (Button, Badge, Card,
 * Input, Field, Modal, Drawer) em vez destas strings.
 */

export const theme = {
  colors: {
    // Mantido para retrocompatibilidade — componentes legados consultam estes nomes
    gradients: {
      // Agora sólidos brand em vez de gradiente laranja→vermelho (mantém prop para caller)
      logo: 'from-brand-500 to-brand-700',
      logoReverse: 'from-brand-700 to-brand-500',
      button: 'from-brand-500 to-brand-600',
      buttonHover: 'from-brand-600 to-brand-700',
      background: 'from-brand-50 to-brand-100 dark:from-brand-900/30 dark:to-brand-800/30',
      backgroundDark: 'from-brand-500 via-brand-600 to-brand-700',
    },
    primary: { main: 'brand-500', dark: 'brand-600', text: 'brand-700', bg: 'brand-50' },
    secondary: { main: 'danger', text: 'danger', bg: 'danger-soft' },
    success: { main: 'success', text: 'success', bg: 'success-soft' },
    warning: { main: 'warning', text: 'warning', bg: 'warning-soft' },
    error: { main: 'danger', text: 'danger', bg: 'danger-soft' },
    info: { main: 'info', text: 'info', bg: 'info-soft' },
  },
  classes: {
    button: {
      primary: 'bg-brand-500 hover:bg-brand-600 text-white font-medium rounded-lg transition-colors',
      secondary: 'bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-ink-title dark:text-ink-title-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt font-medium rounded-lg transition-colors',
      outline: 'border border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt rounded-lg transition-colors',
      danger: 'bg-danger-soft dark:bg-danger-soft-dark text-danger dark:text-danger-dark border border-[rgba(196,50,28,0.25)] hover:bg-[rgba(196,50,28,0.15)] font-medium rounded-lg transition-colors',
      ghost: 'text-ink-text dark:text-ink-text-dark hover:bg-brand-50 dark:hover:bg-[rgba(203,106,30,0.15)] hover:text-brand-700 dark:hover:text-brand-300 rounded-lg transition-colors',
    },
    card: {
      base: 'bg-surface dark:bg-surface-dark rounded-card shadow-sm-warm dark:shadow-sm-dark border border-line dark:border-line-dark',
      elevated: 'bg-surface dark:bg-surface-dark rounded-card shadow-lg-warm dark:shadow-lg-dark border border-line dark:border-line-dark',
      interactive: 'bg-surface dark:bg-surface-dark rounded-card shadow-sm-warm dark:shadow-sm-dark border border-line dark:border-line-dark hover:shadow-md-warm dark:hover:shadow-md-dark transition-shadow cursor-pointer',
    },
    badge: {
      primary: 'bg-brand-50 dark:bg-[rgba(203,106,30,0.15)] text-brand-700 dark:text-brand-300 border border-brand-200 dark:border-[rgba(203,106,30,0.30)] rounded-full px-3 py-1 text-xs font-medium',
      success: 'bg-success-soft dark:bg-success-soft-dark text-success dark:text-success-dark border border-[rgba(30,138,60,0.25)] rounded-full px-3 py-1 text-xs font-medium',
      error: 'bg-danger-soft dark:bg-danger-soft-dark text-danger dark:text-danger-dark border border-[rgba(196,50,28,0.25)] rounded-full px-3 py-1 text-xs font-medium',
      warning: 'bg-warning-soft dark:bg-warning-soft-dark text-warning dark:text-warning-dark border border-[rgba(180,115,10,0.30)] rounded-full px-3 py-1 text-xs font-medium',
      info: 'bg-info-soft dark:bg-info-soft-dark text-info dark:text-info-dark border border-[rgba(43,95,168,0.25)] rounded-full px-3 py-1 text-xs font-medium',
      neutral: 'bg-surface-alt dark:bg-surface-dark-alt text-ink-subtitle dark:text-ink-subtitle-dark border border-line dark:border-line-dark rounded-full px-3 py-1 text-xs font-medium',
    },
    input: {
      base: 'w-full px-4 py-3 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-ink-title dark:text-ink-title-dark',
      error: 'w-full px-4 py-3 bg-surface dark:bg-surface-dark border border-danger dark:border-danger-dark rounded-lg focus:ring-2 focus:ring-danger outline-none text-ink-title dark:text-ink-title-dark',
    },
    alert: {
      success: 'p-4 bg-success-soft dark:bg-success-soft-dark border border-[rgba(30,138,60,0.25)] rounded-lg text-success dark:text-success-dark',
      error: 'p-4 bg-danger-soft dark:bg-danger-soft-dark border border-[rgba(196,50,28,0.25)] rounded-lg text-danger dark:text-danger-dark',
      warning: 'p-4 bg-warning-soft dark:bg-warning-soft-dark border border-[rgba(180,115,10,0.30)] rounded-lg text-warning dark:text-warning-dark',
      info: 'p-4 bg-info-soft dark:bg-info-soft-dark border border-[rgba(43,95,168,0.25)] rounded-lg text-info dark:text-info-dark',
    },
    logo: {
      base: 'w-8 h-8 bg-brand-500 rounded-lg flex items-center justify-center text-white',
      large: 'w-12 h-12 bg-brand-500 rounded-xl flex items-center justify-center text-white shadow-md-warm dark:shadow-md-dark',
      avatar: 'w-8 h-8 bg-brand-500 rounded-full flex items-center justify-center text-white font-medium text-sm',
    },
    stripe: 'bg-brand-500',
    text: {
      expiring: 'text-warning dark:text-warning-dark',
    },
    icon: {
      primary: 'p-3 rounded-full bg-brand-50 dark:bg-[rgba(203,106,30,0.15)] group-hover:bg-brand-100 dark:group-hover:bg-[rgba(203,106,30,0.25)] transition-colors',
      svgPrimary: 'w-6 h-6 text-brand-700 dark:text-brand-300',
    },
    link: {
      primary: 'text-brand-700 dark:text-brand-300 hover:text-brand-800 dark:hover:text-brand-200 font-medium transition-colors',
      secondary: 'text-ink-subtitle dark:text-ink-subtitle-dark hover:text-ink-title dark:hover:text-ink-title-dark transition-colors',
    },
  },
} as const;

export type Theme = typeof theme;
